import {
  type ModelCatalog,
  type ModelTile,
  recommendedFor,
  tileOf,
  tilesFor,
  twinOnRoute,
} from '@/lib/model-catalog';
import { ROUTE_LABELS, formatCost, formatEuros, parseEuros, tariffFor } from '@/lib/model-labels';
import type {
  ModelRoute,
  ModelSeat,
  ModelSeatKey,
  ModelTariff,
  SeatPin,
} from '@matter_extractor/shared';
import { estimateSeatCostEur, seatFingerprint } from '@matter_extractor/shared';
import { TextField } from '@pms-digital/design-system';
import { useState } from 'react';

/**
 * ONE seat of the models, as a card (§11.1): the door, the model behind it, what it costs.
 *
 * It was private to `model-panel.tsx` and is extracted at the smallest, for the second thing that
 * runs on a model and is not a seat of the device: the PLAYER of the server (npc.md §13.4, H28,
 * issue #210), whose model Réglages > Rejeu holds with the very same fields. The card knows no seat
 * KEY: it is handed what names it (`id`, `group`, `title`, `note`), the tiles it may show
 * (`tilesSeat`), and four writers that take a VALUE, so that the panel of the five seats adapts them
 * to its paths and the player's block adapts them to a `ModelSeat` of its own.
 *
 * What the card does is unchanged: a tile moves the whole seat at once, a door change carries its
 * model and its rate, free entry stays one link away.
 */

export interface SeatCardProps {
  /**
   * What names the card: the ids of its fields and radio groups, and its mark `modele-siege-<id>`.
   * A seat key for a seat of the device, `player` for the player of the server.
   */
  id: string;
  /** The small-caps line over the title, the title and the note. No title: a bare card, with no frame. */
  group: string | null;
  title: string | null;
  /** What a bare card (no title) says it is, for its radio groups and its own name. */
  label?: string | undefined;
  note: string | null;
  /** What the operator is holding, not yet written - pin AND tariff. */
  value: ModelSeat;
  /** The environment seed of the first boot - what the deploy started on. Null: no such line. */
  committed: SeatPin | null;
  /** What the base holds right now. */
  stored: SeatPin;
  /** The tiles of this deploy (#175). */
  catalog: ModelCatalog;
  /** The published rates this deploy knows, to prefill a seat that moves. */
  tariffs: readonly ModelTariff[];
  /** The seat whose tiles are offered: every seat may run every tile since #61. */
  tilesSeat: ModelSeatKey;
  /** The seat the estimate of a session is made for, or null: the player has no session type. */
  estimateSeat: ModelSeatKey | null;
  /** The labels of the two free-entry fields. */
  modelLabel: string;
  providerLabel: string;
  /** Joins the seven rows the panel's grid shares (BUILD-925): false stands the card alone. */
  subgrid: boolean;
  /** A model id or an endpoint typed by hand. */
  onModel: (value: string) => void;
  onProvider: (value: string | null) => void;
  /** One euro figure of the tariff. */
  onTariff: (field: 'inputEurPerMillion' | 'outputEurPerMillion', value: number) => void;
  /** A whole seat at once - route, model, endpoint AND tariff. */
  onSeat: (next: ModelSeat) => void;
}

/**
 * One seat, chosen by clicking rather than by typing.
 *
 * The card asks the questions in the order they depend on each other - which
 * DOOR, then which model behind it, then what it costs - because a model id
 * belongs to a door: the router prefixes with a vendor and the direct API does
 * not, so the same weights are two different strings and pinning one against
 * the other is a 404 on every call of the seat.
 *
 * Free entry is one link away and stays a first-class path: the catalogue is
 * not a whitelist, and an unknown model is still accepted with a warning. It
 * also OPENS BY ITSELF when the seat runs on something no tile names - which is
 * the only way the screen can show what is actually pinned rather than showing
 * unselected tiles over a value it is hiding.
 *
 * ## Seven blocks, and a `div role="group"` rather than a `fieldset`
 *
 * The card is a SUBGRID of the panel's grid (BUILD-925), so its top level holds
 * exactly seven children - header, note, provider, model, tariffs, fingerprint,
 * tail - and spans seven rows. Add an eighth block and the span above moves with
 * it, or the last rows collapse into one.
 *
 * The container had to stop being a `<fieldset>` for that, and it was measured
 * rather than assumed: with `grid-template-rows: subgrid` on a fieldset,
 * Chromium lays the `<legend>` out beside the anonymous content box and the rows
 * are NOT shared. The accessible outcome is unchanged: a fieldset+legend maps to
 * `role="group"` named by its legend, which is literally what is spelled out
 * here, and the e2e suite finds the card by that role either way.
 */
export function SeatCard({
  id,
  group,
  title,
  label,
  note,
  value,
  committed,
  stored,
  catalog,
  tariffs,
  tilesSeat,
  estimateSeat,
  modelLabel,
  providerLabel,
  subgrid,
  onModel,
  onProvider,
  onTariff,
  onSeat,
}: SeatCardProps) {
  const [manual, setManual] = useState(false);
  const modelId = `modele-${id}-model`;
  const providerId = `modele-${id}-provider`;
  const titleId = `modele-${id}-titre`;
  const pin = { route: value.route, model: value.model, provider: value.provider };
  const selected = tileOf(catalog, pin);
  // No tile names this pin: the value has to be visible somewhere, so the
  // free-entry fields are the display of last resort rather than a hidden state.
  const custom = selected === null;
  const showManual = manual || custom;
  const drifted = committed !== null && seatFingerprint(stored) !== seatFingerprint(committed);
  const editing = seatFingerprint(stored) !== seatFingerprint(pin);

  /**
   * A model change carries its published RATE with it, when this deploy knows
   * one - and leaves the rate alone when it does not.
   *
   * Leaving the previous model's rate behind would be the silent failure this
   * screen cannot afford: the euro count of every step would be computed at the
   * price of a model the seat stopped running, and nothing on the page would
   * point at it. Wiping it to zero instead would be just as wrong in the other
   * direction - it disarms §11.2 - so an unknown id keeps whatever is there and
   * the operator is the one who decides, which is exactly what the two fields
   * under the tiles are for.
   */
  function pinned(
    next: { route: ModelRoute; model: string; provider: string | null },
    listed: ModelTile['pricing'] = null,
  ): ModelSeat {
    // The provider's own list first (#175), then the grid the params serve.
    const published = listed ?? tariffFor(tariffs, next.model);
    if (published === null) return { ...next, pricing: value.pricing };
    return {
      ...next,
      pricing: {
        inputEurPerMillion: published.inputEurPerMillion,
        outputEurPerMillion: published.outputEurPerMillion,
      },
    };
  }

  /**
   * A door change carries the model with it, in three cases and one order.
   *
   * The TWIN when the catalogue knows the same weights on the other side - that
   * is the literal meaning of the gesture. Failing that, the target door's
   * RECOMMENDED tile, because two catalogue tiles have no twin and keeping their
   * id would put a router spelling on the direct door in one click - a 404 on
   * every call of that seat, and only a warning at the save. Failing both, the
   * id is left exactly as it is: a HAND-TYPED model is never rewritten, because
   * nobody can translate an id nobody declared, and the free-entry field is open
   * showing it.
   */
  function pickRoute(route: ModelRoute) {
    if (route === pin.route) return;
    const twin = twinOnRoute(catalog, pin, route);
    if (twin !== null) {
      onSeat(pinned({ route, model: twin.id, provider: twin.provider }, twin.pricing));
      return;
    }
    const fallback = selected === null ? null : recommendedFor(catalog, tilesSeat, route);
    if (fallback !== null) {
      onSeat(pinned({ route, model: fallback.id, provider: fallback.provider }, fallback.pricing));
      return;
    }
    onSeat(
      pinned({ route, model: pin.model, provider: route === 'anthropic' ? null : pin.provider }),
    );
  }

  function pickModel(tile: ModelTile) {
    onSeat(pinned({ route: tile.route, model: tile.id, provider: tile.provider }, tile.pricing));
  }

  return (
    // 1/7 header, 2/7 note, 3/7 provider, 4/7 model, 5/7 tariffs, 6/7
    // fingerprint, 7/7 tail.
    // biome-ignore lint/a11y/useSemanticElements: a <fieldset> cannot subgrid - measured, see above
    <div
      role="group"
      {...(title === null ? { 'aria-label': label ?? id } : { 'aria-labelledby': titleId })}
      // One mark per seat, `modele-siege-analyst` included (#149): what the
      // capture script probes before it photographs, since a stack of `main`
      // serves three cards and no such mark.
      data-testid={`modele-siege-${id}`}
      data-editing={editing ? 'true' : undefined}
      className={
        // A BARE card (no title) sits inside a frame of its own - the block of the player - and
        // draws none: its door and its model side by side, its rate and its free entry under them.
        title === null
          ? 'grid gap-4 md:grid-cols-2'
          : `grid gap-4 rounded-md border-2 bg-white p-5 ${
              subgrid ? 'xl:row-span-7 xl:grid-rows-subgrid' : ''
            } ${editing ? 'border-bleu-nuit' : 'border-gray-200'}`
      }
    >
      {group === null || title === null ? null : (
        <header className="space-y-1">
          <p className="font-semibold text-caption text-gray-600 uppercase tracking-wide">
            {group}
          </p>
          <p id={titleId} className="font-semibold text-bleu-nuit text-body-lg">
            {title}
          </p>
        </header>
      )}

      {note === null ? null : <p className="text-gray-700 text-small">{note}</p>}

      <div className="space-y-2">
        <p className="font-semibold text-gray-800 text-small">Fournisseur</p>
        <div
          role="radiogroup"
          aria-label={`Fournisseur - ${title ?? label ?? id}`}
          className="grid gap-2"
        >
          {ROUTE_TILE_ORDER.map((route) => (
            <Tile
              key={route}
              name={`${id}-route`}
              checked={pin.route === route}
              onSelect={() => pickRoute(route)}
              title={ROUTE_TILE_LABELS[route]}
              detail={ROUTE_LABELS[route]}
            />
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <p className="font-semibold text-gray-800 text-small">Modèle</p>
        {/*
          ONE TILE PER ROW, and that is the fix rather than a wider grid
          (BUILD-925). Two columns gave an orphan half-width tile at a different
          place depending on how many tiles a door offers, so the checked one had
          no fixed place to look for. A single column is regular whatever the
          catalogue holds tomorrow, and it buys the thing this screen is for: row
          N of one card is the SAME model as row N of the next, so the seats
          compare line by line.
        */}
        <div
          role="radiogroup"
          aria-label={`Modèle - ${title ?? label ?? id}`}
          className="grid gap-2"
        >
          {tilesFor(catalog, tilesSeat, pin.route).map((tile) => (
            <Tile
              key={tile.id}
              name={`${id}-model`}
              checked={selected?.id === tile.id}
              onSelect={() => pickModel(tile)}
              title={tile.label}
              badge={tile.recommended ? 'Recommandé' : undefined}
              detail={tile.pace}
            />
          ))}
        </div>
        {custom ? (
          <p className="text-gray-700 text-small">
            Ce siège tourne sur un identifiant saisi à la main : <code>{value.model}</code>.
          </p>
        ) : null}
      </div>

      <SeatTariffs estimateSeat={estimateSeat} pricing={value.pricing} onTariff={onTariff} />

      {committed === null ? null : (
        <p className="text-caption text-gray-600">
          {/*
          The model id alone rather than the whole pin: the fingerprint of a seat
          also appears in the flotte notice below, and printing the same string
          twice on one screen makes each of them ambiguous - to a reader looking
          for the one that matters, and to anything that reads this page by its
          text. Drift is still measured on the WHOLE pin.
        */}
          Au démarrage du dispositif : <code>{committed.model}</code>
          {drifted ? (
            <span className="ml-2 rounded-md border border-bleu-nuit px-2 py-0.5 text-bleu-nuit">
              déplacé depuis
            </span>
          ) : null}
        </p>
      )}

      {/*
        The tail is ONE grid child - a link, or the free-entry fields - because
        the card spans a fixed seven rows and this is the only block that changes
        shape. Being last, it is also the only one that can differ between cards
        without shifting anything above it.
      */}
      <div>
        {showManual ? null : (
          <button
            type="button"
            onClick={() => setManual(true)}
            className="text-bleu-nuit text-small underline underline-offset-2"
          >
            Saisir un identifiant de modèle à la main
          </button>
        )}

        {showManual ? (
          <div className="space-y-3 border-gray-200 border-t pt-3">
            {/*
              A hand-typed id does NOT carry a rate: nobody can look up a price
              for an id nobody declared. Said here (the field's `help`) rather
              than left to be discovered through a `pricing-missing` warning
              three blocks up, because the operator typing it is the only person
              who can fix it and this is the moment they are looking.
            */}
            <TextField
              id={modelId}
              label={modelLabel}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={value.model}
              onChange={(event) => onModel(event.target.value)}
              help="Un identifiant saisi à la main n’apporte pas son tarif : posez-le au-dessus."
            />
            {/*
              Disabled exactly on the direct route - `seatPinSchema` refuses an
              endpoint beside it - so while it is off its value is always empty
              and the only string on screen is the placeholder. The package field
              draws its own disabled state.
            */}
            <TextField
              id={providerId}
              label={providerLabel}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={value.provider ?? ''}
              disabled={pin.route === 'anthropic'}
              placeholder="aucun hébergeur épinglé"
              onChange={(event) =>
                // An endpoint nobody pinned is `null` - a real option, and not
                // the same thing as an empty string the schema would refuse.
                onProvider(event.target.value === '' ? null : event.target.value)
              }
              {...(pin.route === 'anthropic'
                ? { help: 'Sans objet sur l’API directe : le routeur seul choisit un hébergeur.' }
                : {})}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * What a seat charges, and what that makes one session cost (§11.1).
 *
 * The estimate is recomputed HERE, from the candidate, rather than read off the
 * `estimates` the server sent: the whole point of putting the rate on the seat
 * is that somebody can change it, and a figure that only moved after a save
 * would be stale exactly while it is being decided. The arithmetic is the
 * server's own (`estimateSeatCostEur` in `shared`), so the two cannot disagree.
 *
 * The fields keep their own TEXT while they are being typed, and only push a
 * value that parses. Without that, clearing a field to retype it would write a
 * zero into the candidate for a keystroke - a rate nobody chose, which the
 * screen would then warn about with `pricing-missing`.
 */
function SeatTariffs({
  estimateSeat,
  pricing,
  onTariff,
}: {
  estimateSeat: ModelSeatKey | null;
  pricing: { inputEurPerMillion: number; outputEurPerMillion: number };
  onTariff: (field: 'inputEurPerMillion' | 'outputEurPerMillion', value: number) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="font-semibold text-gray-800 text-small">
        Tarif, en euros par million de jetons
      </p>
      {/*
        Two columns from `sm` and stacked below it: at 375 px two number fields
        side by side put their labels on three lines each, and the card is the
        full width of the page under `xl` anyway - so there is nothing to gain by
        squeezing them.
      */}
      <div className="grid gap-3 sm:grid-cols-2">
        <EuroField
          label="Entrée"
          value={pricing.inputEurPerMillion}
          onNumber={(value) => onTariff('inputEurPerMillion', value)}
        />
        <EuroField
          label="Sortie"
          value={pricing.outputEurPerMillion}
          onNumber={(value) => onTariff('outputEurPerMillion', value)}
        />
      </div>
      {estimateSeat === null ? null : (
        <p className="text-gray-700 text-small">
          Estimation : <strong>{formatCost(estimateSeatCostEur(pricing, estimateSeat))}</strong>
          <span className="text-gray-600"> - indicatif, sur une session type.</span>
        </p>
      )}
    </div>
  );
}

/**
 * One euro amount: a text field that keeps what was typed, and a candidate that
 * only moves on a value that parses.
 *
 * `type="text"` with `inputMode="decimal"` rather than `type="number"`, and the
 * two reasons are the same one twice: a number input reports an EMPTY string
 * for « 1,5 » in a locale whose separator is the comma, and it swallows a
 * scroll wheel over a focused field into a silent change of rate. Both failures
 * end with a euro figure nobody typed.
 */
export function EuroField({
  label,
  value,
  onNumber,
}: {
  label: string;
  value: number;
  onNumber: (value: number) => void;
}) {
  const [typed, setTyped] = useState<string | null>(null);
  // What the field shows: what is being typed, or the candidate. They rejoin as
  // soon as the value parses, so a candidate moved from elsewhere - a tile
  // prefilling its published rate - is visible immediately.
  const shown = typed ?? formatEuros(value);

  return (
    <TextField
      label={label}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      spellCheck={false}
      value={shown}
      onChange={(event) => {
        const text = event.target.value;
        const parsed = parseEuros(text);
        if (parsed === null) {
          setTyped(text);
          return;
        }
        setTyped(null);
        onNumber(parsed);
      }}
      onBlur={() => setTyped(null)}
    />
  );
}

/** How a door is named on its tile - short, the long sentence is underneath. */
const ROUTE_TILE_LABELS: Record<ModelRoute, string> = {
  anthropic: 'Anthropic (direct)',
  openrouter: 'OpenRouter (routeur)',
};

/**
 * The direct API first, and spelled out rather than read off the enum.
 *
 * `modelRouteSchema.options` is in the order the schema happens to declare them,
 * which puts the router ahead of the door every deploy actually starts on - a
 * reading order that says the default is the second choice.
 */
const ROUTE_TILE_ORDER: ModelRoute[] = ['anthropic', 'openrouter'];

/**
 * A tile: a `<label>` around a REAL `<input type="radio">`, kept visible.
 *
 * Both halves of that are deliberate. A native radio rather than a button with
 * `role="radio"`, so the grouping, the arrow keys and the announced state come
 * from the browser instead of from ARIA we would have to keep true by hand. And
 * VISIBLE rather than `sr-only`, because this control is what a click, a capture
 * script and a Playwright `check()` all have to reach - an element the page has
 * deliberately shrunk to a pixel is the class of flake this repository has
 * already paid for once (BUILD-794).
 *
 * « Recommandé » has no box, and that is a legibility decision rather than a
 * contrast one: `border-bleu-nuit` + `text-bleu-nuit` is exactly what « this
 * tile is selected » is drawn with here, so on an unchecked tile a boxed badge
 * read as a selection and on a checked one it dissolved into it. The word
 * survives as the small-caps grey eyebrow the card already uses for its group;
 * the single accent stays with the single thing it means.
 */
function Tile({
  name,
  checked,
  onSelect,
  title,
  detail,
  badge,
}: {
  /** The native group this radio belongs to - one per seat and per question. */
  name: string;
  checked: boolean;
  onSelect: () => void;
  title: string;
  detail: string;
  badge?: string | undefined;
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-3 rounded-md border-2 px-3 py-2 ${
        checked ? 'border-bleu-nuit bg-bleu-nuit-tint' : 'border-gray-300 bg-white'
      }`}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onSelect}
        className="h-4 w-4 shrink-0 accent-bleu-nuit"
      />
      <span className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span
            className={`font-semibold text-small ${checked ? 'text-bleu-nuit' : 'text-gray-900'}`}
          >
            {title}
          </span>
          {badge === undefined ? null : (
            <span className="font-semibold text-caption text-gray-700 uppercase tracking-wide">
              {badge}
            </span>
          )}
        </span>
        {/*
          `gray-700` and not `gray-600`: on the tint of a checked tile the latter
          measures 4,49:1 and misses AA for normal text by a hundredth.
        */}
        <span className="text-caption text-gray-700">{detail}</span>
      </span>
    </label>
  );
}
